<template>
    <div class="inventory-history">
        <div class="page-header">
            <div class="header-left">
                <PageTitle :title="'Historique des inventaires'" :description="(pagination.total || 0) + ' inventaire(s) au total'" />
            </div>
            <button @click="startNewInventory" class="btn-primary">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Lancer un inventaire
            </button>
        </div>

        <div class="filters-section">
            <div class="search-bar">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input v-model="searchQuery" type="text" placeholder="Rechercher par référence..." @input="handleSearch"
                    class="search-input" />
            </div>

            <div class="filters-bar">
                <div class="filter-group">
                    <label>Statut</label>
                    <select v-model="filters.status" @change="loadInventories" class="filter-select">
                        <option value="">Tous</option>
                        <option value="draft">Brouillon</option>
                        <option value="in_progress">En cours</option>
                        <option value="completed">Terminé</option>
                        <option value="approved">Approuvé</option>
                        <option value="rejected">Rejeté</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="table-container">
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <p>Chargement des inventaires...</p>
            </div>

            <div v-else-if="inventories.length === 0" class="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                    <path
                        d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z">
                    </path>
                    <path d="m3.3 7 8.7 5 8.7-5"></path>
                    <path d="M12 22V12"></path>
                </svg>
                <p>Aucun inventaire trouvé</p>
                <button @click="startNewInventory" class="btn-secondary">Créer le premier inventaire</button>
            </div>

            <table v-else class="inventory-table">
                <thead>
                    <tr>
                        <th>Référence</th>
                        <th>Statut</th>
                        <th>Créé par</th>
                        <th>Date création</th>
                        <th>Produits</th>
                        <th>Progression</th>
                        <th>Date validation</th>
                        <th class="actions-column">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="inventory in inventories" :key="inventory.id" class="table-row">
                        <td class="reference-cell">
                            <strong>{{ inventory.reference_number }}</strong>
                        </td>
                        <td>
                            <span :class="['status-badge', `status-${inventory.status}`]">
                                {{ getStatusLabel(inventory.status) }}
                            </span>
                        </td>
                        <td>{{ inventory.user?.name || '-' }}</td>
                        <td>{{ formatDate(inventory.created_at) }}</td>
                        <td>
                            <div class="items-info">
                                <span class="items-total">{{ inventory.total_items }}</span>
                                <span class="items-detail">({{ inventory.counted_items }} comptés)</span>
                            </div>
                        </td>
                        <td>
                            <div class="progress-cell">
                                <div class="progress-bar-small">
                                    <div class="progress-fill-small"
                                        :style="{ width: inventory.progress_percentage + '%' }"></div>
                                </div>
                                <span class="progress-text">{{ inventory.progress_percentage }}%</span>
                            </div>
                        </td>
                        <td>{{ inventory.approved_at ? formatDate(inventory.approved_at) : '-' }}</td>
                        <td class="actions-cell">
                            <button @click="viewInventory(inventory)" class="btn-icon" title="Voir détails">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                            </button>

                            <button v-if="inventory.status === 'draft' || inventory.status === 'in_progress'"
                                @click="continueInventory(inventory)" class="btn-icon btn-primary" title="Continuer">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                            </button>

                            <button v-if="inventory.status === 'completed'" @click="approveInventory(inventory)"
                                class="btn-icon btn-success" title="Approuver">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </button>

                            <button v-if="inventory.status === 'completed'" @click="rejectInventory(inventory)"
                                class="btn-icon btn-danger" title="Rejeter">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>

                            <button v-if="inventory.status === 'draft'" @click="deleteInventory(inventory)"
                                class="btn-icon btn-delete" title="Supprimer">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path
                                        d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                                    </path>
                                </svg>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="pagination.last_page > 1" class="pagination">
            <button @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1"
                class="pagination-btn">
                Précédent
            </button>
            <span class="pagination-info">
                Page {{ pagination.current_page }} sur {{ pagination.last_page }}
            </span>
            <button @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.last_page" class="pagination-btn">
                Suivant
            </button>
        </div>
    </div>
</template>

<script>
import physicalInventoryService from '@/services/physicalInventoryService';
import PageTitle from '@/components/common/PageTitle.vue';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'InventoryHistory',
    components: {
        PageTitle
    },
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            loading: false,
            searchQuery: '',
            searchTimeout: null,
            filters: {
                status: ''
            },
            inventories: [],
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 20,
                total: 0
            }
        }
    },
    mounted() {
        this.loadInventories();
    },
    methods: {
        async loadInventories() {
            this.loading = true;
            try {
                const params = {
                    page: this.pagination.current_page
                };

                if (this.filters.status) {
                    params.status = this.filters.status;
                }

                if (this.searchQuery) {
                    params.search = this.searchQuery;
                }

                const response = await physicalInventoryService.getInventories(params);

                this.inventories = response.data || [];
                this.pagination = {
                    current_page: response.current_page,
                    last_page: response.last_page,
                    per_page: response.per_page,
                    total: response.total
                };
            } catch (error) {
                console.error('Erreur lors du chargement des inventaires:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement des inventaires'
                });
            } finally {
                this.loading = false;
            }
        },

        handleSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.pagination.current_page = 1;
                this.loadInventories();
            }, 500);
        },

        changePage(page) {
            if (page >= 1 && page <= this.pagination.last_page) {
                this.pagination.current_page = page;
                this.loadInventories();
            }
        },

        startNewInventory() {
            this.$router.push({ name: 'inventory-create' });
        },

        viewInventory(inventory) {
            this.$router.push({ name: 'inventory-details', params: { id: inventory.id } });
        },

        continueInventory(inventory) {
            console.log('Continuer inventaire:', inventory);
        },

        async approveInventory(inventory) {
            if (!confirm('Êtes-vous sûr de vouloir approuver cet inventaire ? Les ajustements de stock seront appliqués.')) {
                return;
            }

            try {
                await physicalInventoryService.approveInventory(inventory.id);
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire approuvé avec succès'
                });
                this.loadInventories();
            } catch (error) {
                console.error('Erreur lors de l\'approbation:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de l\'approbation de l\'inventaire'
                });
            }
        },

        async rejectInventory(inventory) {
            const notes = prompt('Raison du rejet (optionnel):');
            if (notes === null) return;

            try {
                await physicalInventoryService.rejectInventory(inventory.id, notes);
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire rejeté'
                });
                this.loadInventories();
            } catch (error) {
                console.error('Erreur lors du rejet:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du rejet de l\'inventaire'
                });
            }
        },

        async deleteInventory(inventory) {
            if (!confirm('Êtes-vous sûr de vouloir supprimer cet inventaire ?')) {
                return;
            }

            try {
                await physicalInventoryService.deleteInventory(inventory.id);
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire supprimé'
                });
                this.loadInventories();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la suppression de l\'inventaire'
                });
            }
        },

        getStatusLabel(status) {
            return physicalInventoryService.getStatusLabel(status);
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
}
</script>

<style scoped>
.inventory-history {
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
}

.header-left {
    flex: 1;
}

.page-title {
    font-size: 28px;
    font-weight: 600;
    color: #1a202c;
    margin: 0 0 8px 0;
}

.page-subtitle {
    color: #718096;
    font-size: 14px;
}

.btn-primary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #3FDA4D;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary:hover {
    background: #35c441;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(63, 218, 77, 0.3);
}

.filters-section {
    background: white;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px;
    background: #f7fafc;
    border-radius: 8px;
}

.search-bar svg {
    color: #718096;
    flex-shrink: 0;
}

.search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    outline: none;
    color: #1a202c;
}

.search-input::placeholder {
    color: #a0aec0;
}

.filters-bar {
    display: flex;
    gap: 16px;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.filter-group label {
    font-size: 13px;
    font-weight: 500;
    color: #4a5568;
}

.filter-select {
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    min-width: 200px;
    cursor: pointer;
    background: white;
}

.filter-select:focus {
    outline: none;
    border-color: #3FDA4D;
}

.table-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.loading-state {
    padding: 80px 40px;
    text-align: center;
}

.spinner {
    width: 40px;
    height: 40px;
    margin: 0 auto 20px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3FDA4D;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-state p {
    color: #718096;
    font-size: 14px;
}

.empty-state {
    padding: 80px 40px;
    text-align: center;
}

.empty-state svg {
    color: #cbd5e0;
    margin-bottom: 20px;
}

.empty-state p {
    color: #718096;
    font-size: 16px;
    margin-bottom: 20px;
}

.btn-secondary {
    padding: 10px 20px;
    background: white;
    color: #3FDA4D;
    border: 2px solid #3FDA4D;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #3FDA4D;
    color: white;
}

.inventory-table {
    width: 100%;
    border-collapse: collapse;
}

.inventory-table thead {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
}

.inventory-table th {
    padding: 16px;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.inventory-table th.actions-column {
    text-align: center;
}

.inventory-table tbody tr {
    border-bottom: 1px solid #f7fafc;
    transition: background-color 0.2s;
}

.inventory-table tbody tr:hover {
    background: #f7fafc;
}

.inventory-table td {
    padding: 16px;
    font-size: 14px;
    color: #2d3748;
}

.reference-cell strong {
    color: #1a202c;
    font-weight: 600;
}

.status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.status-draft {
    background: #e2e8f0;
    color: #4a5568;
}

.status-in_progress {
    background: #bee3f8;
    color: #2c5282;
}

.status-completed {
    background: #fef5e7;
    color: #b7791f;
}

.status-approved {
    background: #c6f6d5;
    color: #22543d;
}

.status-rejected {
    background: #fed7d7;
    color: #742a2a;
}

.items-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.items-total {
    font-weight: 600;
    color: #1a202c;
}

.items-detail {
    font-size: 12px;
    color: #718096;
}

.progress-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.progress-bar-small {
    flex: 1;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    overflow: hidden;
    max-width: 120px;
}

.progress-fill-small {
    height: 100%;
    background: linear-gradient(90deg, #3FDA4D 0%, #35c441 100%);
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 13px;
    font-weight: 600;
    color: #3FDA4D;
    min-width: 40px;
}

.actions-cell {
    text-align: center;
}

.btn-icon {
    padding: 6px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    color: #718096;
    transition: all 0.2s;
    margin: 0 2px;
}

.btn-icon:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
}

.btn-icon.btn-primary {
    color: #3FDA4D;
    border-color: #3FDA4D;
}

.btn-icon.btn-primary:hover {
    background: #3FDA4D;
    color: white;
}

.btn-icon.btn-success {
    color: #48bb78;
    border-color: #48bb78;
}

.btn-icon.btn-success:hover {
    background: #48bb78;
    color: white;
}

.btn-icon.btn-danger {
    color: #e53e3e;
    border-color: #e53e3e;
}

.btn-icon.btn-danger:hover {
    background: #e53e3e;
    color: white;
}

.btn-icon.btn-delete {
    color: #e53e3e;
    border-color: #e53e3e;
}

.btn-icon.btn-delete:hover {
    background: #e53e3e;
    color: white;
}

.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
}

.pagination-btn {
    padding: 8px 16px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
    background: #f7fafc;
    border-color: #3FDA4D;
    color: #3FDA4D;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-info {
    font-size: 14px;
    color: #718096;
}
</style>
